/**
 * Platform admin card (QA helper): reset only the signed-in super admin's own
 * onboarding state and immediately jump back into the onboarding tour.
 */
import React, { useState } from 'react';
import { useRouter } from 'expo-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { adminOnboardingAPI, billingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { confirmDialog, showDialog } from '../../utils/dialogs';

export default function RedoOnboardingCard() {
  const router = useRouter();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { refreshAuth } = useAuth();
  const [resettingOnboarding, setResettingOnboarding] = useState(false);

  const handleRedoOnboarding = async () => {
    const confirmed = await confirmDialog(
      t('platformAdmin.redoOnboardingTitle'),
      t('platformAdmin.redoOnboardingConfirm'),
      t('common.confirm'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      setResettingOnboarding(true);
      await adminOnboardingAPI.resetOwnOnboarding();
      await refreshAuth({ forceBillingRefresh: true, forceUserRefresh: true });
      const freshStatus = await billingAPI.status();
      if (freshStatus.onboarding_completed || freshStatus.intake_completed) {
        throw new Error(t('platformAdmin.redoOnboardingError'));
      }

      const successMessage = t('platformAdmin.redoOnboardingSuccess');
      showToast(successMessage, 'success');

      router.replace(ROUTES.GATED_ONBOARDING as any);
    } catch (error: any) {
      console.error('Failed to reset onboarding', error);
      const message = error?.message || t('platformAdmin.redoOnboardingError');
      showDialog(message);
    } finally {
      setResettingOnboarding(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-0">
        <Text className="text-lg font-semibold text-foreground">
          {t('platformAdmin.redoOnboardingTitle')}
        </Text>
        <Text className="text-sm text-muted-foreground mt-1">
          {t('platformAdmin.redoOnboardingDescription')}
        </Text>
      </CardHeader>
      <CardContent className="gap-4">
        <Button onPress={handleRedoOnboarding} disabled={resettingOnboarding}>
          <Text className="text-white font-medium">
            {resettingOnboarding ? t('common.loading') : t('platformAdmin.redoOnboardingAction')}
          </Text>
        </Button>
      </CardContent>
    </Card>
  );
}
